import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import notificationService from '../../services/notificationService';

const POLL_MS = 60000; // 60s

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * In-app notification bell — shows unread enquiry alerts.
 * Polls the unread count every 60s; loads the full list when opened.
 */
const NotificationBell = () => {
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const pollUnread = useCallback(async () => {
    try {
      setUnread(await notificationService.getUnreadCount());
    } catch {
      /* stay quiet on transient poll failures */
    }
  }, []);

  // Poll on mount + every 60s.
  useEffect(() => {
    pollUnread();
    const id = setInterval(pollUnread, POLL_MS);
    return () => clearInterval(id);
  }, [pollUnread]);

  // Close on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const data = await notificationService.list(20);
      setItems(data.notifications || []);
      setUnread(data.unread ?? 0);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleItemClick = async (n) => {
    if (!n.is_read) {
      try {
        await notificationService.markRead(n.id);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
      } catch { /* no-op */ }
    }
    // Deep-link enquiry alerts straight to the matching lead.
    const contactId = n.payload?.contact_id;
    if (contactId) {
      setOpen(false);
      navigate(`/leads?contact=${contactId}`);
    }
  };

  const markAll = async () => {
    try {
      await notificationService.markAllRead();
      setItems((prev) => prev.map((x) => ({ ...x, is_read: true })));
      setUnread(0);
    } catch { /* no-op */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={toggle}
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        aria-expanded={open}
        className="relative text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md p-1"
      >
        <BellIcon className="h-6 w-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-600 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-gray-900">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-2 w-80 max-w-[90vw] origin-top-right rounded-xl sm-glass shadow-2xl ring-1 ring-white/10 overflow-hidden"
          role="menu"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700/60">
            <span className="text-sm font-semibold text-white">Notifications</span>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && (
              <div className="px-4 py-6 text-center text-sm text-gray-400">Loading…</div>
            )}

            {!loading && items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400">
                No notifications yet.
              </div>
            )}

            {!loading && items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => handleItemClick(n)}
                className={`w-full text-left flex gap-3 px-4 py-3 border-b border-gray-700/40 hover:bg-gray-700/40 transition-colors ${
                  n.is_read ? '' : 'bg-blue-500/5'
                }`}
              >
                <div className="mt-0.5 flex-shrink-0 h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center">
                  <UserPlusIcon className="h-4 w-4 text-blue-400" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white truncate">{n.title}</p>
                    {!n.is_read && <span className="flex-shrink-0 h-2 w-2 rounded-full bg-blue-500" />}
                  </div>
                  {n.body && <p className="text-xs text-gray-400 truncate">{n.body}</p>}
                  <p className="text-[11px] text-gray-500 mt-0.5">{timeAgo(n.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
